import { MoreVert } from '@mui/icons-material'
import { ColumnDef } from '@tanstack/react-table'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Switch } from 'components/Form'
import { LastModifiedCell } from 'components/Table'
import { COLUMN_SIZE } from 'components/Table/constants'
import { KNOWLEDGE_BASE_URL } from 'features/inventory/ad-unit/constants'
import { usePriceGeniusAlwaysOn } from 'features/inventory/ad-unit/hooks/usePriceGeniusAlwaysOn'
import { useAbility } from 'providers/casl'
import { AdUnitsAttributesSchema } from './schema'
import { AdUnitForProductTable, AdUnitsTableProduct } from './types'

export const useColumns = (product: AdUnitsTableProduct): ColumnDef<AdUnitForProductTable>[] => {
  const ability = useAbility()
  const { t } = useTranslation('features/adUnit')
  const { t: tProduct } = useTranslation('features/product')
  const { t: tCommon } = useTranslation('common')

  const { control } = useFormContext<AdUnitsAttributesSchema>()

  const { overwritePriceGeniusState, priceGeniusAlwaysOn } = usePriceGeniusAlwaysOn()
  const cannotUpdatePrebidStack: boolean = ability.cannot('update', 'Product-prebidStack')

  return [
    {
      accessorKey: 'name',
      header: t('list.header.adUnitName'),
    },
    {
      accessorKey: 'divId',
      header: t('list.header.divId'),
    },
    {
      accessorKey: 'path',
      header: t('list.header.adUnitPath'),
    },
    {
      accessorKey: `${product}Enabled`,
      cell: ({
        cell: { getValue },
        row: {
          original: { id, outOfPage },
        },
      }) => (
        <Switch<AdUnitsAttributesSchema>
          control={control}
          disabled={
            cannotUpdatePrebidStack ||
            (product === 'priceGenius' && overwritePriceGeniusState(product)) ||
            (product === 'viewabilityTools' && outOfPage)
          }
          name={`adUnitsAttributes.${id}`}
          value={getValue()}
          {...(product === 'priceGenius' &&
            overwritePriceGeniusState(product) && {
              tooltip: {
                content: t('form.priceGenius.enabled.tooltip'),
                link: {
                  title: tCommon('learnMore'),
                  url: KNOWLEDGE_BASE_URL,
                },
              },
              tooltipMode: 'wrap',
              uiValueOverride: priceGeniusAlwaysOn,
            })}
        />
      ),
      header: tProduct(`product.${product}`),
      meta: { nonClickable: true },
    },
    {
      accessorKey: 'updatedAt',
      cell: props => <LastModifiedCell {...props} />,
      header: t('list.header.lastModified'),
    },
    {
      cell: () => <MoreVert />,
      header: t('list.header.actions'),
      id: 'actions',
      meta: {
        centered: true,
        nonClickable: true,
      },
      size: COLUMN_SIZE.SM,
    },
  ]
}
